<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { useAccountSecuritySettings } from "@/composables/Security/useAccountSecuritySettings";
import { useCommoditySecuritySettings } from "@/composables/Security/useCommoditySecuritySettings";
import { useAuthStore } from "@/stores/auth";
import { useParametersStore } from "@/stores/parameters";
import { usePortalSecuritySettings } from "@/composables/Security/usePortalSecuritySettings";
import { useMiscSecuritySettings } from "@/composables/Security/useMiscSecuritySettings";
import ContractsSecuritySettings from "@/helpers/Security/ContractsSecuritySettings";
import PaymentsSecuritySettings from "@/helpers/Security/PaymentsSecuritySettings";
import PurchasesSecuritySettings from "@/helpers/Security/PurchasesSecuritySettings";
import OrdersSecuritySettings from "@/helpers/Security/OrdersSecuritySettings";

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

interface DrawerResourceLink {
  title: string;
  icon: string;
  to?: string;
  href?: string;
  target?: string;
  visible: boolean;
}

const authStore = useAuthStore();
const parametersStore = useParametersStore();
const accountSecuritySettings = useAccountSecuritySettings();
const commoditySecuritySettings = useCommoditySecuritySettings();
const securitySettings = usePortalSecuritySettings();
const miscSecuritySettings = useMiscSecuritySettings();
const contractsSecuritySettings = ContractsSecuritySettings.getInstance();
const paymentsSecuritySettings = PaymentsSecuritySettings.getInstance();
const purchasesSecuritySettings = PurchasesSecuritySettings.getInstance();
const ordersSecuritySettings = OrdersSecuritySettings.getInstance();
const { smAndUp, xs } = useDisplay();

const isInstalledAsPwa = window.matchMedia("(display-mode: standalone)").matches;
const drawerState = ref(props.modelValue);

watch(
  () => props.modelValue,
  (value) => {
    drawerState.value = value;
  }
);

watch(drawerState, (value) => {
  emit("update:modelValue", value);
});

watch(
  smAndUp,
  (value) => {
    if (value && authStore.isAuthenticated) {
      drawerState.value = true;
    }
  },
  { immediate: true }
);

const drawerLinks = computed(() => {
  return [
    {
      title: "Home",
      icon: "mdi-home",
      to: "/home",
      visible: authStore.isAuthenticated,
    },
  ].filter((link) => link.visible);
});

const accountLinks = computed(() => {
  if (!accountSecuritySettings.showAccountMenu.value) {
    return [];
  }

  return [
    {
      title: "Summary",
      icon: "mdi-view-dashboard-outline",
      to: "/summary/view",
      visible: accountSecuritySettings.showAccountSummaryPage.value,
    },
    {
      title: "Address Book",
      icon: "mdi-map-marker-outline",
      to: "/addresses/view",
      visible: accountSecuritySettings.showAddressesPage.value,
    },
    {
      title: "Buyers",
      icon: "mdi-account-group-outline",
      to: "/account/buyers",
      visible: accountSecuritySettings.showBuyersPage.value,
    },
    {
      title: "Profile",
      icon: "mdi-account-details-outline",
      to: "/profile/view",
      visible: accountSecuritySettings.showProfilePage.value,
    },
    {
      title: "Login History",
      icon: "mdi-history",
      to: "/logins/view",
      visible: accountSecuritySettings.showLoginHistoryPage.value,
    },
  ].filter((link) => link.visible);
});

const purchasesLinks = computed(() => {
  return [
    {
      title: "Credit Activity",
      icon: "mdi-cash-clock",
      to: "/credit-activity/view",
      visible: purchasesSecuritySettings.showViewCreditActivityPage,
    },
    {
      title: "Purchases",
      icon: "mdi-receipt-text-outline",
      to: "/purchases/view",
      visible: purchasesSecuritySettings.showViewPurchasesPage,
    },
    {
      title: "Transactions",
      icon: "mdi-swap-horizontal",
      to: "/transactions/view",
      visible: purchasesSecuritySettings.showViewTransactionsPage,
    },
    {
      title: "Statements",
      icon: "mdi-file-document-outline",
      to: "/statements/view",
      visible: purchasesSecuritySettings.showViewStatementsPage,
    },
    {
      title: "Purchase Summary",
      icon: "mdi-chart-box-outline",
      to: "/purchase-summary/view",
      visible: purchasesSecuritySettings.showViewPurchaseSummaryPage,
    },
  ].filter((link) => link.visible);
});

const contractsLinks = computed(() => {
  return [
    {
      title: "Bookings",
      icon: "mdi-calendar-clock-outline",
      to: "/bookings/view",
      visible: contractsSecuritySettings.showViewBookingsPage,
    },
    {
      title: "Prepaids",
      icon: "mdi-cash-refund",
      to: "/prepaids/view",
      visible: contractsSecuritySettings.showViewPrepaidsPage,
    },
  ].filter((link) => link.visible);
});

const managerLinks = computed(() => {
  return [
    {
      title: "Sales Overview",
      icon: "mdi-chart-line",
      to: "/manager/salesOverview",
      visible: securitySettings.managerAccountSelected.value,
    },
  ].filter((link) => link.visible);
});

const ordersLinks = computed(() => {
  return [
    {
      title: "Create an Order",
      icon: "mdi-cart-plus",
      to: "/orders/create",
      visible: ordersSecuritySettings.showCreateOrderPage,
    },
    {
      title: "Orders",
      icon: "mdi-storefront-outline",
      to: "/orders/view",
      visible: ordersSecuritySettings.showViewOrdersPage,
    },
  ].filter((link) => link.visible);
});

const paymentsLinks = computed(() => {
  return [
    {
      title: "Make a Payment",
      icon: "mdi-cash-plus",
      to: "/payment/create",
      visible: paymentsSecuritySettings.showCreatePaymentPage,
    },
    {
      title: "Automatic Payments",
      icon: "mdi-auto-fix",
      to: "/payment/automaticPayments",
      visible: paymentsSecuritySettings.showAutoPaymentsPage,
    },
    {
      title: "Pending Payments",
      icon: "mdi-timer-sand",
      to: "/payment/pending",
      visible: paymentsSecuritySettings.showPendingPaymentsPage,
    },
    {
      title: "Payment Sources",
      icon: "mdi-bank-outline",
      to: "/payment/sources",
      visible: paymentsSecuritySettings.showPaymentSourcesPage,
    },
    {
      title: "Payment History",
      icon: "mdi-credit-card-check-outline",
      to: "/payment/history",
      visible: paymentsSecuritySettings.showPaymentHistoryPage,
    },
  ].filter((link) => link.visible);
});

const commodityLinks = computed(() => {
  if (!commoditySecuritySettings.showCommodityMenu.value) {
    return [];
  }

  return [
    {
      title: "Cash Bids",
      icon: "mdi-cash-multiple",
      to: "/bids/view",
      visible: commoditySecuritySettings.showCashBidsPage.value,
    },
    {
      title: "Balances",
      icon: "mdi-scale-balance",
      to: "/balances/view",
      visible: commoditySecuritySettings.showBalancesPage.value,
    },
    {
      title: "Activity",
      icon: "mdi-chart-timeline-variant",
      to: "/activity/view",
      visible: commoditySecuritySettings.showActivityPage.value,
    },
    {
      title: "Offers",
      icon: "mdi-file-document-outline",
      to: "/offers/view",
      visible: commoditySecuritySettings.showOffersPage.value,
    },
    {
      title: "Contracts",
      icon: "mdi-note-text-outline",
      to: "/contracts/view",
      visible: commoditySecuritySettings.showContractsPage.value,
    },
    {
      title: "Contracts Needing to be Signed",
      icon: "mdi-draw-pen",
      to: "/unsigned-contracts/view",
      visible: commoditySecuritySettings.showContractsPage.value,
    },
    {
      title: "Tickets",
      icon: "mdi-ticket-outline",
      to: "/tickets/view",
      visible: commoditySecuritySettings.showTicketsPage.value,
    },
    {
      title: "Grain Bank",
      icon: "mdi-silo",
      to: "/grain-bank/view",
      visible: commoditySecuritySettings.showGrainBankPage.value,
    },
    {
      title: "Proof of Yield",
      icon: "mdi-sprout-outline",
      to: "/proof-of-yield/view",
      visible: commoditySecuritySettings.showProofOfYieldPage.value,
    },
  ].filter((link) => link.visible);
});

const fieldAlyticsDisplayName = computed(() => {
  return (
    parametersStore.parameterSettings.fieldAlyticsDisplayName?.trim() ||
    "FieldAlytics"
  );
});

const miscLinks = computed<DrawerResourceLink[]>(() => {
  return [
    {
      title: "Contact Us",
      icon: "mdi-lifebuoy",
      to: "/contact",
      visible: miscSecuritySettings.showContactUsPage.value,
    },
    {
      title: "Licenses",
      icon: "mdi-card-account-details-outline",
      to: "/licenses",
      visible: miscSecuritySettings.showLicensesPage.value,
    },
    {
      title: fieldAlyticsDisplayName.value,
      icon: "mdi-sprout",
      to: "/fieldalytics",
      visible: miscSecuritySettings.showFieldAlyticsPage.value,
    },
    {
      title: "Login to CFS",
      icon: "mdi-currency-usd",
      href: parametersStore.parameterSettings.cfsPortalUrl,
      visible:
        parametersStore.parameterSettings.useCfs &&
        !!parametersStore.parameterSettings.cfsPortalUrl &&
        !securitySettings.managerAccountSelected.value,
    },
    {
      title: "Install App",
      icon: "mdi-cellphone-arrow-down",
      to: "/install-app-how-to",
      target: "_blank",
      visible: xs.value && !isInstalledAsPwa,
    },
  ].filter((link) => link.visible);
});
</script>

<template>
  <v-navigation-drawer v-model="drawerState" app>
    <div class="pa-4">
      <v-img src="/img/main-logo.png" max-width="220" />
    </div>

    <v-list nav density="comfortable">
      <v-list-item
        v-for="link in drawerLinks"
        :key="link.title"
        :prepend-icon="link.icon"
        :to="link.to"
      >
        <v-list-item-title>{{ link.title }}</v-list-item-title>
      </v-list-item>

      <template v-if="accountLinks.length">
        <v-list-subheader>Account</v-list-subheader>
        <v-list-item
          v-for="link in accountLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="purchasesLinks.length">
        <v-list-subheader>Purchases</v-list-subheader>
        <v-list-item
          v-for="link in purchasesLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="contractsLinks.length">
        <v-list-subheader>Contracts</v-list-subheader>
        <v-list-item
          v-for="link in contractsLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="managerLinks.length">
        <v-list-subheader>Manager</v-list-subheader>
        <v-list-item
          v-for="link in managerLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="ordersLinks.length">
        <v-list-subheader>Orders</v-list-subheader>
        <v-list-item
          v-for="link in ordersLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="paymentsLinks.length">
        <v-list-subheader>Payments</v-list-subheader>
        <v-list-item
          v-for="link in paymentsLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="commodityLinks.length">
        <v-list-subheader>Commodity</v-list-subheader>
        <v-list-item
          v-for="link in commodityLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="miscLinks.length">
        <v-list-subheader>Resources</v-list-subheader>
        <v-list-item
          v-for="link in miscLinks"
          :key="link.title"
          :prepend-icon="link.icon"
          :to="link.to"
          :href="link.href"
          :target="link.target"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>

    <div v-if="parametersStore.isAnyLoading">
      <v-skeleton-loader type="list-item-avatar, list-item@2" />
      <v-skeleton-loader type="list-item-avatar, list-item@3" />
      <v-skeleton-loader type="list-item-avatar, list-item" />
    </div>

    <template #append>
      <div class="pa-4 text-body-2">
        <div v-if="authStore.selectedAccount?.name" class="font-weight-medium">
          {{ authStore.selectedAccount.name }}
        </div>
        <div v-else-if="authStore.webLogin.webLoginUserName">
          {{ authStore.webLogin.webLoginUserName }}
        </div>
      </div>
    </template>
  </v-navigation-drawer>
</template>
